import { useMemo, useState } from "react";
import Select from "react-select";

export interface LocalAssociation {
  id: string;
  name: string;
}

/** Landesverband => zugeordnete Ortsvereine, siehe MembersRecruitMembersController */
export type Associations = Record<string, LocalAssociation[]>;

export interface AssociationSelection {
  regionalAssociation: string | null;
  localAssociation: string | null;
}

interface Option {
  value: string;
  label: string;
}

interface AssociationSelectsProps {
  associations: Associations;
  onChange: (selection: AssociationSelection) => void;
}

const sharedSelectProps = {
  unstyled: true,
  // Pflichtfelder: react-select rendert dann ein required-Input, die Auswahl lässt
  // sich aber nicht mehr auf "nichts" zurücksetzen - genau wie zuvor mit select2.
  required: true,
  className: "form-select-container",
  classNamePrefix: "form-select",
  noOptionsMessage: () => I18n.t("js.plugins.select2.no_match"),
};

function requiredMarker() {
  return <abbr title={I18n.t("js.required")}> *</abbr>;
}

/**
 * Die beiden kaskadierenden Dropdowns des MwM-Formulars. Die Auswahl des
 * Landesverbands bestimmt die verfügbaren Ortsvereine.
 *
 * react-select rendert für beide Felder ein Hidden-Input mit dem jeweiligen `name`,
 * daher landen die Werte wie zuvor ganz normal in den `hgmp_order`-Parametern.
 */
export default function AssociationSelects({
  associations,
  onChange,
}: AssociationSelectsProps) {
  const [regional, setRegional] = useState<Option | null>(null);
  const [local, setLocal] = useState<Option | null>(null);

  const regionalOptions = useMemo(
    () =>
      Object.keys(associations).map((name) => ({ value: name, label: name })),
    [associations],
  );

  const localOptions = useMemo(() => {
    if (!regional) return [];

    return (associations[regional.value] || []).map(({ id, name }) => ({
      value: id,
      label: name,
    }));
  }, [associations, regional]);

  function select(nextRegional: Option | null, nextLocal: Option | null) {
    setRegional(nextRegional);
    setLocal(nextLocal);
    onChange({
      regionalAssociation: nextRegional?.label ?? null,
      localAssociation: nextLocal?.label ?? null,
    });
  }

  return (
    <>
      <label
        className="control-label"
        htmlFor="hgmp_order_regional_association"
      >
        Landesverband:
        {requiredMarker()}
      </label>
      <div className="controls">
        <Select<Option>
          {...sharedSelectProps}
          inputId="hgmp_order_regional_association"
          name="hgmp_order[regional_association]"
          options={regionalOptions}
          value={regional}
          onChange={(option) => select(option, null)}
          placeholder="Bitte den Landesverband auswählen oder suchen"
        />
      </div>

      <label
        className="control-label"
        htmlFor="hgmp_order_local_association_id"
      >
        Ortsverein:
        {requiredMarker()}
      </label>
      <div className="controls">
        <Select<Option>
          {...sharedSelectProps}
          inputId="hgmp_order_local_association_id"
          name="hgmp_order[local_association_id]"
          options={localOptions}
          value={local}
          onChange={(option) => select(regional, option)}
          isDisabled={!regional}
          placeholder={
            regional
              ? "Bitte den Ortsverein auswählen oder suchen"
              : "Bitte erst den Landesverband auswählen"
          }
        />
      </div>
    </>
  );
}
